import { Controller } from "@hotwired/stimulus"
import { patch, post } from "@rails/request.js"

/**
 * Inline Edit Controller
 *
 * Click-to-edit for existing records. On error, escalates to modal.
 *
 * Usage:
 *   <div data-controller="inline-edit"
 *        data-inline-edit-url-value="/account/slots/123"
 *        data-inline-edit-field-value="name"
 *        data-inline-edit-model-name-value="staffing_plans_slot"
 *        data-inline-edit-fallback-url-value="/account/slots/123/edit">
 *     <span data-inline-edit-target="display"
 *           data-action="click->inline-edit#edit">Slot Name</span>
 *     <input data-inline-edit-target="input" class="hidden" />
 *   </div>
 */
export default class extends Controller {
  static targets = ["display", "input", "hint", "trigger"]

  static values = {
    url: String,
    field: String,
    fallbackUrl: String,
    confirmMessage: { type: String, default: "" },
    saveOnClickOutside: { type: Boolean, default: true },
    refreshOnSuccess: { type: Boolean, default: true },
    refreshMode: { type: String, default: "current" },
    placeholder: { type: String, default: "Click to edit" },
    method: { type: String, default: "patch" },
    modelName: { type: String, default: "" },
    saving: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
    disableNearestDragHandle: { type: Boolean, default: false },
    dragHandleClass: { type: String, default: "reorder-handle" }
  }

  connect() {
    this.originalValue = this.displayTarget?.textContent.trim() || ""
    this.handleInput = this.handleInput.bind(this)
    this.dragHandleElement = this.disableNearestDragHandleValue ? this.element.closest(`.${this.dragHandleClassValue}`) : null
    this.dragHandleWasEnabled = !!this.dragHandleElement
  }

  disconnect() {
    this.setDragHandleEnabled(true)
    this.unbindEvents()
  }

  bindEvents() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("click", this.handleClickOutside)
    this.inputTarget?.addEventListener("input", this.handleInput)
  }

  unbindEvents() {
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("click", this.handleClickOutside)
    this.inputTarget?.removeEventListener("input", this.handleInput)
  }

  handleInput() {
    this.autosizeInput()
  }

  handleKeydown(event) {
    if (!this.editingValue) return
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    } else if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.save()
    }
  }

  handleClickOutside(event) {
    if (!this.editingValue) return
    if (this.element.contains(event.target)) return
    if (event.target.closest('[data-turbo-frame="modal"]')) {
      // Opening a modal is an explicit escalation path, not an implicit save.
      this.cancel()
      return
    }
    const modalContent = this.element.closest('[data-modal-target~="content"]')
    if (modalContent && !modalContent.contains(event.target)) {
      // Dismissing the surrounding modal is a cancel action, not an implicit save.
      this.cancel()
      return
    }
    this.finishImplicitEdit()
  }

  finishImplicitEdit() {
    if (!this.editingValue) return
    if (!this.saveOnClickOutsideValue) {
      this.cancel()
      return
    }

    // Implicit exit: empty value is not persisted; only explicit confirm (Enter) saves empty
    const newValue = this.inputTarget?.value.trim() ?? ""
    if (newValue === "") {
      this.cancel()
      return
    }
    this.save()
  }

  edit(event) {
    if (this.editingValue || this.savingValue) return
    event?.preventDefault()

    // Re-read current value from display (in case of Turbo morph)
    const currentValue = this.displayTarget?.textContent.trim()
    if (currentValue && currentValue !== this.placeholderValue) {
      this.originalValue = currentValue
    }

    this.editingValue = true
    this.bindEvents()
    this.setDragHandleEnabled(false)

    this.displayTarget.classList.add("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.add("hidden")
    this.inputTarget.classList.remove("hidden")
    if (this.hasHintTarget) this.hintTarget.classList.remove("hidden")
    this.inputTarget.value = this.originalValue
    this.inputTarget.disabled = false
    this.autosizeInput()
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  cancel(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.displayTarget.textContent = this.originalValue || this.placeholderValue
    this.finishEdit()
    this.dispatch("cancelled")
  }

  consumeEvent(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  finishEdit() {
    this.editingValue = false
    this.unbindEvents()
    this.setDragHandleEnabled(true)
    if (this.inputTarget?.tagName === "TEXTAREA") this.inputTarget.style.height = ""
    this.inputTarget?.classList.add("hidden")
    this.displayTarget?.classList.remove("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.remove("hidden")
    if (this.hasHintTarget) this.hintTarget.classList.add("hidden")
  }

  async save() {
    if (this.savingValue) return

    const newValue = this.inputTarget?.value.trim() || ""
    if (newValue === this.originalValue) {
      this.cancel()
      return
    }

    if (this.confirmMessageValue && !window.confirm(this.confirmMessageValue)) {
      this.inputTarget.focus()
      return
    }

    this.savingValue = true
    this.element.classList.add("opacity-50")
    this.dispatch("saving", { detail: { field: this.fieldValue, value: newValue } })

    let saved = false

    try {
      const body = this.modelNameValue
        ? { [this.modelNameValue]: { [this.fieldValue]: newValue } }
        : { [this.fieldValue]: newValue }

      const requestMethod = this.methodValue === "post" ? post : patch
      const response = await requestMethod(this.urlValue, {
        body: JSON.stringify({ ...body, refresh_mode: this.refreshModeValue }),
        contentType: "application/json",
        responseKind: "turbo-stream"
      })

      if (response.ok) {
        saved = true
        this.originalValue = newValue
        this.displayTarget.textContent = newValue
        if (this.refreshOnSuccessValue) {
          // Keep input visible but disabled while waiting for Turbo refresh.
          this.inputTarget.disabled = true
        } else {
          this.finishEdit()
        }
        this.dispatch("saved", { detail: { field: this.fieldValue, value: newValue } })
      } else {
        this.handleSaveFailure(newValue)
      }
    } catch (error) {
      console.error("Inline edit failed:", error)
      this.handleSaveFailure(newValue)
    } finally {
      this.savingValue = false
      this.element.classList.remove("opacity-50")
      this.dispatch("settled", { detail: { field: this.fieldValue, value: newValue, saved } })
    }
  }

  handleSaveFailure(value) {
    this.finishEdit()
    const failureEvent = this.dispatch("failed", { detail: { field: this.fieldValue, value } })
    if (!failureEvent.defaultPrevented) this.escalateToModal()
  }

  escalateToModal() {
    if (!this.fallbackUrlValue) {
      this.showError()
      return
    }

    const link = document.createElement("a")
    const url = new URL(this.fallbackUrlValue, window.location.origin)
    url.searchParams.set("return_to", window.location.href)
    link.href = url.toString()
    link.dataset.turboFrame = "modal"
    // Keep the synthetic modal link inside the current interaction surface so
    // an already-open modal does not interpret the click as an outside close.
    this.element.appendChild(link)
    link.click()
    link.remove()
  }

  showError() {
    this.element.classList.add("ring-2", "ring-red-500")
    setTimeout(() => this.element.classList.remove("ring-2", "ring-red-500"), 2000)
  }

  autosizeInput() {
    if (this.inputTarget?.tagName !== "TEXTAREA") return

    this.inputTarget.style.height = "0px"
    this.inputTarget.style.height = `${this.inputTarget.scrollHeight}px`
  }

  setDragHandleEnabled(enabled) {
    if (!this.dragHandleElement || !this.dragHandleWasEnabled) return

    this.dragHandleElement.classList.toggle(this.dragHandleClassValue, enabled)
    this.dragHandleElement.classList.toggle("hover:cursor-move", enabled)
  }
}
